import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { utilitiesPayloadType } from '../../../../pages/ApplicationSummaryPage/useApplicationSummaryPage';
import {
  CommonDropdown,
  CommonFormTextField,
} from '../../../FormComponents/CommonFields';
import useUnlockStateFileModal from './useUnlockStateFileModal';

const UnlockStateFileModal = (props: {
  popup: utilitiesPayloadType | undefined;
  handleModalClose: () => void;
}) => {
  const { handleModalClose, popup } = props;
  const {
    formik,
    selectApplicationFieldError,
    selectedEnvironmentFieldError,
    storageAccountNameFieldError,
    tfStateFilePathFieldError,
    listApplicationsData,
    listApplicationsLoading,
  } = useUnlockStateFileModal(handleModalClose, popup);
  const {
    selectedApplication,
    selectedEnvironment,
    storageAccountName,
    tfStateFilePath,
  } = formik.values;

  const storageAccountNameHelpText =
    formik.values.selectedApplication && formik.values.selectedEnvironment
      ? `'${formik.values.selectedApplication.applicationName}-${formik.values.selectedEnvironment.name}'`
      : "'application-env'";

  return (
    <Box>
      <form onSubmit={formik.handleSubmit}>
        <DialogTitle>Unlock State File</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex' }}>
            <Box sx={{ flex: 1 }}>
              <CommonDropdown
                componentID="selectedApplication"
                label="Select an Application"
                data={listApplicationsData}
                value={selectedApplication || null}
                fieldError={selectApplicationFieldError}
                helperText={
                  selectApplicationFieldError &&
                  formik.errors.selectedApplication
                    ? formik.errors.selectedApplication
                    : 'Select an application.'
                }
                fullHelpText="Please select an application from the dropdown menu."
                loading={listApplicationsLoading}
                required
                noOptionsText="No Options Found."
                onChangeHandler={(e, value) => {
                  formik.setFieldValue(
                    'selectedApplication',
                    value || undefined
                  );
                  formik.setFieldValue('selectedEnvironment', null);
                }}
                onBlurHandler={formik.handleBlur}
                getOptionLabel={(option) =>
                  option?.applicationName || 'No Application Name'
                }
                getOptionSelected={(o, v) =>
                  o.applicationName === v.applicationName
                }
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <CommonDropdown
                dropdownDisabled={
                  formik.values.selectedApplication === undefined
                }
                componentID="selectedEnvironment"
                label="Select an Environment"
                data={
                  formik.values.selectedApplication?.environments?.filter(
                    (e) => !e?.deleteStatus && !e?.notReady
                  ) ?? []
                }
                value={selectedEnvironment || null}
                fieldError={selectedEnvironmentFieldError}
                helperText={
                  selectedEnvironmentFieldError &&
                  formik.errors.selectedEnvironment
                    ? formik.errors.selectedEnvironment
                    : 'Select an environment.'
                }
                fullHelpText="Please select an environment from the dropdown menu."
                loading={false}
                required
                noOptionsText="No Options Found."
                onChangeHandler={(e, value) => {
                  formik.setFieldValue(
                    'selectedEnvironment',
                    value || undefined
                  );
                }}
                onBlurHandler={formik.handleBlur}
                getOptionLabel={(option) =>
                  option?.name || 'No Environment Name'
                }
                getOptionSelected={(o, v) => o?.name === v?.name}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <Box sx={{ flex: 1 }}>
              <CommonFormTextField
                textFieldDisabled={
                  formik.values.selectedApplication === undefined
                }
                componentID="storageAccountName"
                label="Storage Account Name"
                value={storageAccountName || ''}
                onChangeHandler={formik.handleChange}
                onBlurHandler={formik.handleBlur}
                fieldError={storageAccountNameFieldError}
                helperText={
                  storageAccountNameFieldError &&
                  formik.errors.storageAccountName
                    ? formik.errors.storageAccountName
                    : 'Enter a storage account name.'
                }
                fullHelpText={`Input the value for the key 'TF.Backend.StorageAccount', you can access it inside the ${storageAccountNameHelpText} variable group.`}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <CommonFormTextField
                textFieldDisabled={
                  formik.values.selectedApplication === undefined
                }
                componentID="tfStateFilePath"
                label="Terraform State File Path"
                value={tfStateFilePath || ''}
                onChangeHandler={formik.handleChange}
                onBlurHandler={formik.handleBlur}
                fieldError={tfStateFilePathFieldError}
                helperText={
                  tfStateFilePathFieldError && formik.errors.tfStateFilePath
                    ? formik.errors.tfStateFilePath
                    : 'Enter terraform state file path.'
                }
                fullHelpText="Please enter terraform state file path which can be found in your failed pipeline error log."
              />
            </Box>
          </Box>
          <DialogActions>
            <Button
              disabled={
                !(formik.isValid && formik.dirty) || formik.isSubmitting
              }
              variant="contained"
              type="submit"
              endIcon={
                formik.isSubmitting ? (
                  <CircularProgress size="14px" sx={{ marginLeft: '0.5rem' }} />
                ) : undefined
              }
            >
              Unlock
            </Button>
          </DialogActions>
        </DialogContent>
      </form>
    </Box>
  );
};

export default UnlockStateFileModal;
